import React from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { getCurrencySymbol } from "@/utils/currency";
import type { CampaignInfluencerFromDB, PaymentValidationErrors } from "../types";

export interface FormattedInfluencerOption {
  id: string;
  name: string;
  handle: string;
  platform: string;
}

interface InfluencerCampaignSelectorProps {
  influencers?: FormattedInfluencerOption[];
  loadingInfluencers: boolean;
  influencerId: string;
  onInfluencerChange: (id: string) => void;
  campaignInfluencerId: string;
  onCampaignInfluencerChange: (id: string) => void;
  filteredCampaignInfluencers: CampaignInfluencerFromDB[];
  loadingCampaigns: boolean;
  baseFee: string;
  onBaseFeeChange: (val: string) => void;
  userCountry?: string | null;
  errors: PaymentValidationErrors;
}

export const InfluencerCampaignSelector: React.FC<InfluencerCampaignSelectorProps> = ({
  influencers,
  loadingInfluencers,
  influencerId,
  onInfluencerChange,
  campaignInfluencerId,
  onCampaignInfluencerChange,
  filteredCampaignInfluencers,
  loadingCampaigns,
  baseFee,
  onBaseFeeChange,
  userCountry,
  errors,
}) => {
  return (
    <>
      {/* Influencer Name */}
      <div>
        <label className="block font-medium mb-1">
          Influencer Name <span className="text-destructive">*</span>
        </label>
        <Select
          value={influencerId}
          onValueChange={onInfluencerChange}
          disabled={loadingInfluencers}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                loadingInfluencers
                  ? "Loading influencers..."
                  : "Select Influencer"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {influencers?.map((influencer) => (
              <SelectItem key={influencer.id} value={influencer.id}>
                {influencer.name} ({influencer.handle}) - {influencer.platform}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.influencerId && (
          <p className="text-destructive text-xs mt-1">{errors.influencerId}</p>
        )}
      </div>

      {/* Campaign */}
      <div>
        <label className="block font-medium mb-1">
          Campaign <span className="text-destructive">*</span>
        </label>
        <Select
          value={campaignInfluencerId}
          onValueChange={onCampaignInfluencerChange}
          disabled={!influencerId || loadingCampaigns}
        >
          <SelectTrigger>
            <SelectValue
              placeholder={
                !influencerId
                  ? "Select Influencer first"
                  : loadingCampaigns
                    ? "Loading campaigns..."
                    : filteredCampaignInfluencers.length === 0
                      ? "No campaigns found"
                      : "Select Campaign"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {filteredCampaignInfluencers.map((ci) => (
              <SelectItem key={ci.id} value={ci.id}>
                {ci.campaigns?.name || "Unknown Campaign"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {errors.campaignInfluencerId && (
          <p className="text-destructive text-xs mt-1">
            {errors.campaignInfluencerId}
          </p>
        )}
      </div>

      {/* Base Fee Amount */}
      <div>
        <label className="block font-medium mb-1">
          Base Fee Amount ({getCurrencySymbol(userCountry)}){" "}
          <span className="text-destructive">*</span>
        </label>
        <Input
          type="number"
          min={1}
          step="0.01"
          placeholder="Enter base fee amount (excluding GST and TDS)"
          value={baseFee}
          onChange={(e) => onBaseFeeChange(e.target.value)}
        />
        <p className="text-sm text-gray-600 mt-1">
          This is the base fee amount before adding GST or deducting TDS.
        </p>
        {errors.baseFee && (
          <p className="text-destructive text-xs mt-1">{errors.baseFee}</p>
        )}
      </div>
    </>
  );
};
